import i18n from './i18n.js';
import { loadImage, checkBrowserSupport } from './lib/image-loader.js';
import { writeDPItoPNG, writeDPItoJPEG, writeDPItoWebP } from './lib/dpi.js';
import { createZip } from './lib/zip.js';
import { formatFileSize, showToast } from './utils.js';
import { UploadArea } from './components/upload-area.js';
import { ImageInfo } from './components/image-info.js';
import { TemplateSelector } from './components/template-selector.js';
import { OutputSettings } from './components/output-settings.js';
import { DownloadBtn } from './components/download-btn.js';
import { PreviewComparison } from './components/preview-comparison.js';

export class App {
  constructor() {
    this.images = [];
    this.activeIndex = 0;
    this.target = null;
    this.outputSettings = { format: 'png', quality: 1.0, mimeType: 'image/png', extension: 'png' };
    this.worker = null;
    this.isProcessing = false;
    this._previewToken = 0;
    this._previewTimeout = null;
  }

  async init() {
    await i18n.init();

    /* Apply i18n to static HTML elements (hero, footer, etc.) */
    document.querySelectorAll('[data-i18n]').forEach(function(el) {
      el.textContent = i18n.t(el.getAttribute('data-i18n'));
    });

    if (!checkBrowserSupport()) {
      showToast(i18n.t('upload.error.browser'), 'error');
      document.querySelector('#app-content').innerHTML = '<div class="container" style="text-align:center;padding:4rem 0;"><p style="color:var(--color-error);">' + i18n.t('upload.error.browser') + '</p></div>';
      return;
    }
    this.initWorker();
    this.initComponents();
    this.bindI18nEvents();
    this.bindLangSwitcher();
  }

  initWorker() {
    var self = this;
    this.worker = new Worker(new URL('./workers/resample.worker.js', import.meta.url), { type: 'module' });
    this.worker.onerror = function(e) {
      console.error('Worker error:', e);
      showToast(i18n.t('error.worker'), 'error');
      self.downloadBtn.setState('ready');
      self.isProcessing = false;
    };
  }

  initComponents() {
    var self = this;
    this.uploadArea = new UploadArea(
      document.querySelector('#upload-container'),
      function(files) { self.onFilesChanged(files); },
      function(index, file) { self.onSelectFile(index, file); }
    );
    this.imageInfo = new ImageInfo(document.querySelector('#image-info-container'));
    this.preview = new PreviewComparison(document.querySelector('#preview-container'));
    this.templateSelector = new TemplateSelector(document.querySelector('#template-container'), function(t) {
      self.target = t;
      self.schedulePreview();
    });
    this.outputSettingsComponent = new OutputSettings(document.querySelector('#output-container'), function(s) {
      self.outputSettings = s;
      self.schedulePreview();
    });
    this.downloadBtn = new DownloadBtn(document.querySelector('#download-container'), function() { self.onDownload(); });
  }

  /* ======== File management ======== */

  async onFilesChanged(files) {
    this.images.forEach(function(im) { if (im.bitmap && im.bitmap.close) im.bitmap.close(); });
    this.images = [];
    this.preview.hide();

    if (files.length === 0) {
      this.imageInfo.hide();
      this.downloadBtn.setState('disabled');
      return;
    }

    for (var i = 0; i < files.length; i++) {
      try {
        var im = await loadImage(files[i]);
        this.images.push(im);
      } catch (err) {
        var msg = err.message === 'UNSUPPORTED_TYPE' ? i18n.t('upload.error.type')
          : err.message === 'SIZE_EXCEEDED' ? i18n.t('upload.error.size')
          : i18n.t('upload.error.decode');
        showToast(files[i].name + ': ' + msg, 'error');
      }
    }

    if (this.images.length === 0) {
      this.uploadArea.reset();
      return;
    }

    this.activeIndex = 0;
    this.showActiveImage();
    this.updateDownloadBtnText();
    this.downloadBtn.setState('ready');
    this.schedulePreview();
  }

  onSelectFile(index, file) {
    this.activeIndex = index;
    this.showActiveImage();
    this.schedulePreview();
  }

  showActiveImage() {
    var im = this.images[this.activeIndex];
    if (!im) return;
    this.imageInfo.show(im);
    this.preview.showBefore(im);
  }

  updateDownloadBtnText() {
    if (this.images.length <= 1) {
      this.downloadBtn.setButtonText(i18n.t('download.button'));
    } else {
      this.downloadBtn.setButtonText(i18n.t('download.button.batch', { count: this.images.length }));
    }
  }

  /* ======== Auto-preview (debounced) ======== */

  schedulePreview() {
    if (this._previewTimeout) clearTimeout(this._previewTimeout);
    this._previewTimeout = setTimeout(this._doPreview.bind(this), 500);
  }

  async _doPreview() {
    var im = this.images[this.activeIndex];
    if (!im || this.isProcessing) return;

    /* Cancel any in-flight preview */
    this._previewToken++;
    var token = this._previewToken;

    this.preview.showLoading();

    try {
      var tw = im.width;
      var th = im.height;

      if (this.target) {
        var sc = Math.min(this.target.width / im.width, this.target.height / im.height);
        tw = Math.round(sc < 1 ? im.width : this.target.width);
        th = Math.round(sc < 1 ? im.height : this.target.height);
      }

      /* Only run preview if resize actually needed (different from original) */
      if (tw === im.width && th === im.height) {
        if (token === this._previewToken) {
          /* Show original as "after" since no resize needed */
          this.preview.showAfter(URL.createObjectURL(im.originalBlob));
        }
        return;
      }

      var canvas = document.createElement('canvas');
      canvas.width = im.width;
      canvas.height = im.height;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(im.bitmap, 0, 0);
      var srcData = ctx.getImageData(0, 0, im.width, im.height);

      var pixelData = await this.processInWorker(
        srcData.data.buffer, im.width, im.height, tw, th
      );

      /* Check cancellation before continuing */
      if (token !== this._previewToken) return;

      var oc = document.createElement('canvas');
      oc.width = tw;
      oc.height = th;
      var octx = oc.getContext('2d');
      octx.putImageData(new ImageData(new Uint8ClampedArray(pixelData), tw, th), 0, 0);

      var blob = await new Promise(function(resolve, reject) {
        oc.toBlob(function(b) { if (b) resolve(b); else reject(new Error('Blob creation failed')); }, this.outputSettings.mimeType, this.outputSettings.quality);
      }.bind(this));

      /* Final cancellation check */
      if (token !== this._previewToken) return;

      var blobUrl = URL.createObjectURL(blob);
      this.preview.showAfter(blobUrl);
    } catch (err) {
      /* Preview errors are silently ignored — a later attempt may succeed */
      console.warn('Preview processing failed:', err);
    }
  }

  /* ======== Download ======== */

  async onDownload() {
    if (this.isProcessing || this.images.length === 0) return;
    this.isProcessing = true;
    this.downloadBtn.setState('processing');

    try {
      var output = this.outputSettings;
      var dpi = this.target ? (this.target.dpi || 300) : 300;
      var results = [];

      for (var i = 0; i < this.images.length; i++) {
        var im = this.images[i];
        var tw = im.width;
        var th = im.height;

        if (this.target) {
          var sc = Math.min(this.target.width / im.width, this.target.height / im.height);
          tw = Math.round(sc < 1 ? im.width : this.target.width);
          th = Math.round(sc < 1 ? im.height : this.target.height);
        }

        var canvas = document.createElement('canvas');
        canvas.width = im.width;
        canvas.height = im.height;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(im.bitmap, 0, 0);
        var srcData = ctx.getImageData(0, 0, im.width, im.height);

        var pixelData = await this.processInWorker(
          srcData.data.buffer, im.width, im.height, tw, th
        );

        var oc = document.createElement('canvas');
        oc.width = tw;
        oc.height = th;
        var octx = oc.getContext('2d');
        octx.putImageData(new ImageData(new Uint8ClampedArray(pixelData), tw, th), 0, 0);

        var blob = await new Promise(function(resolve, reject) {
          oc.toBlob(function(b) { if (b) resolve(b); else reject(new Error('Blob creation failed')); }, output.mimeType, output.quality);
        });

        var finalBlob = output.format === 'png' ? await writeDPItoPNG(blob, dpi)
          : output.format === 'jpeg' ? await writeDPItoJPEG(blob, dpi)
          : await writeDPItoWebP(blob, dpi);

        var fname = im.file.name.replace(/\.[^.]+$/, '') + '_300dpi.' + output.extension;
        results.push({ name: fname, data: await finalBlob.arrayBuffer() });
      }

      /* Download */
      if (results.length === 1) {
        var r = results[0];
        var dlUrl = URL.createObjectURL(new Blob([r.data], { type: output.mimeType }));
        var a = document.createElement('a');
        a.href = dlUrl; a.download = r.name; a.click();
        URL.revokeObjectURL(dlUrl);
      } else {
        var zipBlob = createZip(results);
        var dateStr = new Date().toISOString().slice(0, 10);
        var zipUrl = URL.createObjectURL(zipBlob);
        var a = document.createElement('a');
        a.href = zipUrl; a.download = 'pod_images_' + dateStr + '.zip'; a.click();
        URL.revokeObjectURL(zipUrl);
      }

      /* Output info for active image */
      var activeResult = results[this.activeIndex];
      if (activeResult) {
        var activeIm = this.images[this.activeIndex];
        var outW = activeIm.width, outH = activeIm.height;
        if (this.target) {
          var s = Math.min(this.target.width / activeIm.width, this.target.height / activeIm.height);
          outW = Math.round(s < 1 ? activeIm.width : this.target.width);
          outH = Math.round(s < 1 ? activeIm.height : this.target.height);
        }
        this.showOutputInfo(outW, outH, dpi, activeResult.data.byteLength, output);
      }

      this.downloadBtn.setState('ready');
      this.isProcessing = false;
      showToast(i18n.t('download.completed'), 'success');
    } catch (err) {
      console.error('Processing error:', err);
      showToast(i18n.t('error.worker'), 'error');
      this.downloadBtn.setState('ready');
      this.isProcessing = false;
    }
  }

  processInWorker(pixels, sw, sh, tw, th) {
    var self = this;
    return new Promise(function(resolve, reject) {
      var id = Date.now();
      var handler = function(e) {
        if (e.data.id === id) {
          self.worker.removeEventListener('message', handler);
          if (e.data.success) resolve(e.data.pixels);
          else reject(new Error(e.data.error));
        }
      };
      self.worker.addEventListener('message', handler);
      self.worker.postMessage({ id: id, pixels: pixels, srcWidth: sw, srcHeight: sh, targetWidth: tw, targetHeight: th }, [pixels]);
    });
  }

  showOutputInfo(w, h, dpi, size, output) {
    var c = document.getElementById('output-info');
    if (!c) return;
    c.classList.add('visible');
    c.innerHTML =
      '<div class="output-info-row"><span>DPI:</span><span>' + dpi + ' DPI</span></div>' +
      '<div class="output-info-row"><span>Dimensions:</span><span>' + w + ' × ' + h + ' px</span></div>' +
      '<div class="output-info-row"><span>Format:</span><span>' + output.format.toUpperCase() + '</span></div>' +
      '<div class="output-info-row"><span>File size:</span><span>' + formatFileSize(size) + '</span></div>';
  }

  /* ======== I18n & language ======== */

  bindI18nEvents() {
    var self = this;
    document.addEventListener('langchange', function() {
      document.querySelectorAll('[data-i18n]').forEach(function(el) {
        el.textContent = i18n.t(el.getAttribute('data-i18n'));
      });
      self.uploadArea.updateI18n();
      self.imageInfo.updateI18n();
      self.templateSelector.updateI18n();
      self.outputSettingsComponent.updateI18n();
      self.downloadBtn.updateI18n();
      self.preview.updateI18n();
      self.updateDownloadBtnText();
    });
  }

  bindLangSwitcher() {
    var self = this;
    var btns = document.querySelectorAll('.lang-btn');
    btns.forEach(function(btn) {
      btn.addEventListener('click', async function() {
        var lang = btn.dataset.lang;
        await i18n.setLanguage(lang);
        btns.forEach(function(b) { b.classList.remove('active'); });
        btn.classList.add('active');
      });
    });
    var cur = i18n.getLanguage();
    btns.forEach(function(btn) { if (btn.dataset.lang === cur) btn.classList.add('active'); });
  }
}
